import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth-context';
import { commissionAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { TrendingUp, Wallet, Target } from 'lucide-react';
import { format } from 'date-fns';

const CommissionsPage = () => {
  const { user } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState({ total_commission: 0, total_transactions: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [commRes, summaryRes] = await Promise.all([commissionAPI.getCommissions(100), commissionAPI.getSummary()]);
      setCommissions(commRes.data);
      setSummary(summaryRes.data);
    } catch (error) { console.error('Error fetching commissions:', error); } finally { setLoading(false); }
  };

  const avgCommission = summary.total_transactions > 0 ? (summary.total_commission / summary.total_transactions).toFixed(2) : 0;

  return (
    <DashboardLayout>
      <div className="space-y-5 animate-fade-in" data-testid="commissions-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Commission Earnings</h1>
          <p className="text-sm text-slate-500 mt-0.5">Track your commission earnings from recharges</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="stat-accent-line green">
            <Card className="border border-slate-100 shadow-sm rounded-xl">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em] mb-2">Total Commission</p>
                    <p className="text-2xl font-bold text-slate-900 font-mono">&#8377;{summary.total_commission?.toLocaleString('en-IN')}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-500"><Wallet className="w-[18px] h-[18px] text-white" /></div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="stat-accent-line blue">
            <Card className="border border-slate-100 shadow-sm rounded-xl">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em] mb-2">Total Transactions</p>
                    <p className="text-2xl font-bold text-slate-900 font-mono">{summary.total_transactions}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#4680ff]"><TrendingUp className="w-[18px] h-[18px] text-white" /></div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="stat-accent-line purple">
            <Card className="border border-slate-100 shadow-sm rounded-xl">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em] mb-2">Avg. Commission</p>
                    <p className="text-2xl font-bold text-slate-900 font-mono">&#8377;{avgCommission}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-500"><Target className="w-[18px] h-[18px] text-white" /></div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Commission Rate Banner */}
        <div className="p-4 rounded-xl bg-blue-50/60 border border-blue-100 flex items-center gap-3 text-sm">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-4 h-4 text-[#4680ff]" />
          </div>
          <span className="text-slate-600">
            Your Rate: <strong className="text-[#4680ff] font-mono">{user?.role === 'retailer' ? user?.retailer_commission : user?.distributor_commission}%</strong>
            <span className="text-slate-400 ml-1">({user?.role === 'retailer' ? 'Direct commission' : 'Override commission'} on successful recharges)</span>
          </span>
        </div>

        {/* Table */}
        <Card className="border border-slate-100 shadow-sm rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">Commission History <span className="text-slate-400 font-normal">({commissions.length})</span></p>
          </div>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-5 space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}</div>
            ) : commissions.length === 0 ? (
              <div className="text-center py-16">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                <p className="text-sm font-medium text-slate-400">No commissions earned yet</p>
                <p className="text-xs text-slate-300 mt-1">Complete recharges to start earning!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/30 hover:bg-slate-50/30">
                      <TableHead className="pl-5">Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Recharge ID</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commissions.map((c) => (
                      <TableRow key={c.id} className="table-row-hover border-b border-slate-50">
                        <TableCell className="pl-5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border capitalize ${
                            c.type === 'retailer' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-blue-50 text-blue-700 border-blue-100'
                          }`}>{c.type}</span>
                        </TableCell>
                        <TableCell className="text-right font-bold font-mono text-emerald-600">+&#8377;{c.amount.toFixed(2)}</TableCell>
                        <TableCell><code className="text-xs bg-slate-100 px-2 py-1 rounded-md font-mono text-slate-500">{c.recharge_id.slice(0, 8)}...</code></TableCell>
                        <TableCell className="text-sm text-slate-400">{format(new Date(c.created_at), 'dd MMM yyyy, hh:mm a')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default CommissionsPage;
